import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Inbox, Trash2, Mail, Clock, CheckCircle, XCircle, Eye, X, Phone, FileText } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';

const statusColors = {
  pending: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/20', label: 'Pending' },
  reviewed: { bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/20', label: 'Reviewed' },
  shortlisted: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/20', label: 'Shortlisted' },
  rejected: { bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/20', label: 'Rejected' },
};

const AdminApplicationsPage = () => {
  const [applications, setApplications] = useState([]);
  const [selectedApp, setSelectedApp] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const token = localStorage.getItem('token');
      const res = await axios.get(`${apiUrl}/api/applications`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setApplications(Array.isArray(res.data) ? res.data : res.data?.data || []);
    } catch (err) {
      toast.error('Failed to fetch applications');
    }
  };

  const updateStatus = async (id, status) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const token = localStorage.getItem('token');
      await axios.put(`${apiUrl}/api/applications/${id}`, { status }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (status === 'shortlisted') {
        toast.success('Candidate shortlisted & automated congratulatory email dispatched!');
      } else if (status === 'rejected') {
        toast.info('Candidate updated & feedback notification email dispatched.');
      } else {
        toast.success(`Application marked as ${status}`);
      }

      fetchApplications();
      if (selectedApp?.id === id) {
        setSelectedApp({ ...selectedApp, status });
      }
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const deleteApplication = async (id) => {
    if (!window.confirm('Delete this application permanently?')) return;
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const token = localStorage.getItem('token');
      await axios.delete(`${apiUrl}/api/applications/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Application deleted');
      fetchApplications();
      if (selectedApp?.id === id) setSelectedApp(null);
    } catch (err) {
      toast.error('Failed to delete application');
    }
  };

  const filteredApps = filter === 'all' 
    ? applications 
    : applications.filter(a => a.status === filter);

  const counts = {
    all: applications.length,
    pending: applications.filter(a => a.status === 'pending').length,
    reviewed: applications.filter(a => a.status === 'reviewed').length,
    shortlisted: applications.filter(a => a.status === 'shortlisted').length,
    rejected: applications.filter(a => a.status === 'rejected').length,
  };

  return (
    <div className="space-y-8 animate-fade-in pb-12 font-sans">
      
      {/* Header Panel */}
      <div className="admin-hero-panel flex flex-col lg:flex-row lg:items-center justify-between gap-6">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 text-xs font-mono mb-2">
            <Inbox className="w-3.5 h-3.5" />
            <span>Job Applications Inbox</span>
          </div>
          <h1 className="admin-heading text-3xl sm:text-4xl font-black text-white tracking-tight">
            Candidate Submissions
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            {counts.pending > 0 
              ? `${counts.pending} pending candidate dossier${counts.pending > 1 ? 's' : ''} awaiting evaluation.` 
              : 'All candidate applications reviewed.'}
          </p>
        </div>

        <div className="flex items-center gap-4">
          <div className="admin-mini-stat bg-white/5 border border-white/10 px-4 py-2 rounded-xl text-center">
            <span className="text-xl font-bold text-white font-mono">{applications.length}</span>
            <small className="block text-[10px] text-slate-400 font-mono uppercase">Total Inbound</small>
          </div>
          <div className="admin-mini-stat bg-white/5 border border-white/10 px-4 py-2 rounded-xl text-center">
            <span className="text-xl font-bold text-amber-400 font-mono">{counts.pending}</span>
            <small className="block text-[10px] text-slate-400 font-mono uppercase">Pending</small>
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 flex-wrap">
        {['all', 'pending', 'reviewed', 'shortlisted', 'rejected'].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider transition-all border ${
              filter === f
                ? 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30 shadow-lg shadow-indigo-500/10'
                : 'bg-white/5 text-slate-400 border-transparent hover:border-white/10 hover:text-white'
            }`}
          >
            {f} ({counts[f]})
          </button>
        ))}
      </div>

      {/* Applications Grid / List */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <AnimatePresence mode="popLayout">
          {filteredApps.map((app, idx) => {
            const status = statusColors[app.status] || statusColors.pending;
            return (
              <motion.div
                key={app.id || idx}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ delay: idx * 0.03 }}
                onClick={() => setSelectedApp(app)}
                className={`glass-card p-5 cursor-pointer group hover:border-indigo-500/30 transition-all flex flex-col justify-between ${
                  selectedApp?.id === app.id ? 'border-indigo-500/40 ring-1 ring-indigo-500/20' : 'border-white/5'
                }`}
              >
                <div>
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center text-indigo-400 font-bold shrink-0">
                        {app.applicant_name ? app.applicant_name[0].toUpperCase() : 'C'}
                      </div>
                      <div>
                        <h3 className="text-base font-bold text-white font-heading group-hover:text-indigo-400 transition-colors">
                          {app.applicant_name}
                        </h3>
                        <p className="text-xs text-indigo-300 font-mono mt-0.5">
                          {app.job_title || 'Engineering Role'}
                        </p>
                      </div>
                    </div>

                    <span className={`px-2.5 py-1 rounded-full text-[10px] font-mono font-bold uppercase tracking-wider border ${status.bg} ${status.text} ${status.border}`}>
                      {status.label}
                    </span>
                  </div>

                  <div className="flex flex-wrap items-center gap-3 text-xs text-slate-400 mt-2 font-mono">
                    <span className="flex items-center gap-1.5"><Mail size={13} className="text-slate-500" /> {app.email}</span>
                    {app.phone && <span className="flex items-center gap-1.5"><Phone size={13} className="text-slate-500" /> {app.phone}</span>}
                  </div>
                </div>

                <div className="mt-4 pt-3 border-t border-white/5 flex items-center justify-between text-[11px] font-mono text-slate-500">
                  <span>{app.years_exp || 0} Yrs Experience</span>
                  <span className="flex items-center gap-1">
                    <Clock size={11} /> {new Date(app.created_at || Date.now()).toLocaleDateString()}
                  </span>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>

      {!filteredApps.length && (
        <div className="glass-card p-12 text-center text-slate-400 font-mono text-sm">
          No candidate dossiers match the "{filter}" filter.
        </div>
      )}

      {/* Submission Viewer Modal */}
      <AnimatePresence>
        {selectedApp && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 overflow-y-auto">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedApp(null)}
              className="fixed inset-0 bg-black/80 backdrop-blur-md"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 16 }}
              className="glass-panel relative z-10 w-full max-w-xl max-h-[85vh] overflow-y-auto border-white/10 rounded-3xl my-auto p-0 shadow-2xl"
            >
              {/* Header */}
              <div className="p-6 border-b border-white/10 flex items-start justify-between bg-gradient-to-r from-indigo-500/10 via-purple-500/5 to-transparent">
                <div className="flex items-center gap-3.5">
                  <div className="w-12 h-12 rounded-2xl bg-indigo-600/20 border border-indigo-500/30 flex items-center justify-center text-indigo-400 font-bold text-lg shadow-lg">
                    {selectedApp.applicant_name ? selectedApp.applicant_name[0].toUpperCase() : 'C'}
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white font-heading">{selectedApp.applicant_name}</h2>
                    <p className="text-xs font-mono text-indigo-400 mt-0.5">{selectedApp.job_title || 'Candidate Application'}</p>
                  </div>
                </div>

                <button 
                  onClick={() => setSelectedApp(null)} 
                  className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-slate-400 hover:text-white transition-colors"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Body Content */}
              <div className="p-6 space-y-6">
                
                {/* Information Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <div className="p-3.5 rounded-2xl bg-white/[0.03] border border-white/5">
                    <span className="text-[10px] font-mono uppercase text-slate-500 block mb-1">Email Address</span>
                    <p className="text-xs font-bold text-white truncate font-mono">{selectedApp.email}</p>
                  </div>
                  <div className="p-3.5 rounded-2xl bg-white/[0.03] border border-white/5">
                    <span className="text-[10px] font-mono uppercase text-slate-500 block mb-1">Phone Contact</span>
                    <p className="text-xs font-bold text-white font-mono">{selectedApp.phone || 'N/A'}</p>
                  </div>
                  <div className="p-3.5 rounded-2xl bg-white/[0.03] border border-white/5 col-span-2 sm:col-span-1">
                    <span className="text-[10px] font-mono uppercase text-slate-500 block mb-1">Experience</span>
                    <p className="text-xs font-bold text-emerald-400 font-mono">{selectedApp.years_exp || 0} Years</p>
                  </div>
                </div>

                {/* Cover Note Section */}
                <div>
                  <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400 block mb-2 flex items-center gap-1.5">
                    <FileText size={13} className="text-indigo-400" /> Candidate Cover Note
                  </span>
                  <div className="p-4 rounded-2xl bg-black/40 border border-white/10 text-xs text-slate-300 leading-relaxed font-sans whitespace-pre-wrap max-h-48 overflow-y-auto">
                    {selectedApp.cover_letter || 'No cover letter note provided.'}
                  </div>
                </div>

                {/* Decision Actions */}
                <div className="space-y-3 pt-4 border-t border-white/10">
                  <span className="text-[10px] font-mono uppercase text-slate-400 tracking-wider block text-center">Update Application Status</span>
                  
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      onClick={() => updateStatus(selectedApp.id, 'shortlisted')}
                      className="py-2.5 px-3 rounded-xl bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border border-emerald-500/20 font-bold text-xs flex items-center justify-center gap-1.5 transition-all"
                    >
                      <CheckCircle size={14} /> Shortlist
                    </button>
                    <button
                      onClick={() => updateStatus(selectedApp.id, 'reviewed')}
                      className="py-2.5 px-3 rounded-xl bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 border border-blue-500/20 font-bold text-xs flex items-center justify-center gap-1.5 transition-all"
                    >
                      <Eye size={14} /> Review
                    </button>
                    <button
                      onClick={() => updateStatus(selectedApp.id, 'rejected')}
                      className="py-2.5 px-3 rounded-xl bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 font-bold text-xs flex items-center justify-center gap-1.5 transition-all"
                    >
                      <XCircle size={14} /> Reject
                    </button>
                  </div>

                  <button
                    onClick={() => deleteApplication(selectedApp.id)}
                    className="w-full py-2.5 px-4 rounded-xl bg-white/5 hover:bg-red-500/10 text-slate-400 hover:text-red-400 border border-white/5 transition-colors text-xs font-medium flex items-center justify-center gap-2 mt-2"
                  >
                    <Trash2 size={14} /> Delete Application Record
                  </button>
                </div>

              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminApplicationsPage;
